import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Heartbeat, Heart } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── CHECK-IN DEFINITIONS BY TYPE ───
const CHECKIN_DEFS = {
  gravida: {
    greeting: (name) => `Olá, ${name} 💛\nHoje é dia de feedback.\nA gravidez é feita de fases diferentes. Algumas semanas são mais estáveis, outras trazem novos sintomas ou dúvidas. Este momento serve para avaliarmos tudo com atenção.\nPartilha o que conseguires. Não há respostas certas ou erradas.`,
    fields: [
      { key: 'semana_geral', label: 'Como foi a tua semana em geral?', type: 'scale', min: 'Muito difícil', max: 'Muito boa' },
      { key: 'peso', label: 'Peso desta semana', type: 'number', unit: 'kg', optional: true },
      { key: 'apetite', label: 'Apetite', type: 'scale', min: 'Sem apetite', max: 'Muito aumentado' },
      { key: 'nauseas', label: 'Náuseas ou vómitos', type: 'choice', options: ['Sem náuseas', 'Melhorou', 'Igual', 'Piorou'] },
      { key: 'desejos', label: 'Desejos ou aversões alimentares', type: 'text', optional: true },
      { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
      { key: 'sintomas', label: 'Sintomas esta semana', type: 'multi', options: ['Azia', 'Inchaço', 'Cansaço', 'Retenção de líquidos', 'Nenhum', 'Outro'] },
      { key: 'exercicio', label: 'Movimento ou exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não esta semana'] },
      { key: 'novidades_clinicas', label: 'Novidades clínicas', type: 'text', optional: true },
      { key: 'partilha', label: 'Algo que queiras partilhar ou perguntar', type: 'text', optional: true },
    ],
  },
  pos_parto: {
    greeting: (name) => `Olá, ${name} 🤱\nNo meio das rotinas, das noites curtas e das exigências do dia a dia, é fácil esqueceres-te de ti.\nEste momento serve para isso: parar e avaliar como estás.\nPartilha o que conseguires. Cuidar de ti não é egoísmo.`,
    fields: [
      { key: 'semana_geral', label: 'Como foi a tua semana em geral?', type: 'scale', min: 'Muito difícil', max: 'Muito boa' },
      { key: 'peso', label: 'Peso desta semana', type: 'number', unit: 'kg', optional: true },
      { key: 'gestao_alimentar', label: 'Gestão alimentar esta semana', type: 'scale', min: 'Muito difícil', max: 'Correu muito bem' },
      { key: 'fome', label: 'Fome e saciedade', type: 'choice', options: ['Fome excessiva', 'Fome normal', 'Pouco apetite', 'Irregular'] },
      { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
      { key: 'energia', label: 'Energia e cansaço', type: 'scale', min: 'Exausta', max: 'Com energia' },
      { key: 'sintomas', label: 'Sintomas ou desconfortos', type: 'multi', options: ['Cansaço excessivo', 'Queda de cabelo', 'Inchaço', 'Dores', 'Nenhum', 'Outro'] },
      { key: 'amamentacao', label: 'Amamentação', type: 'choice', options: ['Sim, corre bem', 'Sim, com dificuldades', 'Não amamenta'] },
      { key: 'dificuldades_amamentacao', label: 'Dificuldades na amamentação', type: 'text', optional: true, condition: { field: 'amamentacao', value: 'Sim, com dificuldades' } },
      { key: 'exercicio', label: 'Movimento ou exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não esta semana'] },
      { key: 'novidades_clinicas', label: 'Novidades clínicas', type: 'text', optional: true },
      { key: 'partilha', label: 'Algo que queiras partilhar ou perguntar', type: 'text', optional: true },
    ],
  },
  pre_concepcao: {
    greeting: (name) => `Olá, ${name} 💛\nHoje é dia de feedback.\nNesta fase, cada detalhe conta. O objetivo é preparar o teu corpo com consistência, equilíbrio e dados reais.\nPartilha o que conseguires. Regularidade, energia, ciclo, sono e digestão são sinais importantes.`,
    fields: [
      { key: 'semana_geral', label: 'Como foi a tua semana em geral?', type: 'scale', min: 'Muito difícil', max: 'Muito boa' },
      { key: 'peso', label: 'Peso desta semana', type: 'number', unit: 'kg', optional: true },
      { key: 'gestao_alimentar', label: 'Gestão alimentar esta semana', type: 'scale', min: 'Muito difícil', max: 'Correu muito bem' },
      { key: 'energia', label: 'Energia e disposição', type: 'scale', min: 'Sem energia', max: 'Com muita energia' },
      { key: 'sono', label: 'Sono', type: 'choice', options: ['Bom', 'Regular', 'Mau'] },
      { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
      { key: 'ciclo', label: 'Ciclo menstrual', type: 'choice', options: ['Regular', 'Irregular', 'Sem alterações', 'Não aplicável'] },
      { key: 'sintomas', label: 'Sintomas físicos ou emocionais', type: 'multi', options: ['Cansaço', 'Ansiedade', 'Inchaço', 'Cólicas', 'Alterações de humor', 'Nenhum', 'Outro'] },
      { key: 'exercicio', label: 'Movimento ou exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não esta semana'] },
      { key: 'novidades_clinicas', label: 'Novidades clínicas', type: 'text', optional: true },
      { key: 'partilha', label: 'Algo que queiras partilhar ou perguntar', type: 'text', optional: true },
    ],
  },
  introducao_alimentar: {
    greeting: (name) => `Olá, ${name} 💛\nHoje é dia de feedback do teu Super Bebé!\nVamos acompanhar de perto a sua evolução e ajustar sempre que for necessário.\nPartilha o que correu bem e o que foi mais desafiante. Não existe feedback perfeito. É assim que conseguimos ajustar com intenção e segurança.`,
    fields: [
      { key: 'semana_geral', label: 'Como correu a semana alimentar do bebé em geral?', type: 'scale', min: 'Muito difícil', max: 'Correu muito bem' },
      { key: 'metodo', label: 'Método de introdução alimentar utilizado', type: 'choice', options: ['BLW', 'Papas e purés', 'Misto', 'Ainda a experimentar'] },
      { key: 'alimentos_novos', label: 'Alimentos novos introduzidos esta semana', type: 'text', optional: true },
      { key: 'reacao', label: 'Como reagiu o bebé aos alimentos novos?', type: 'choice', options: ['Aceitou bem', 'Aceitou com alguma resistência', 'Recusou', 'Não introduzi alimentos novos'] },
      { key: 'reacoes', label: 'Reações a registar', type: 'multi', options: ['Alergia ou reação cutânea', 'Vómito', 'Engasgamento', 'Recusa persistente', 'Nenhuma', 'Outra'] },
      { key: 'num_refeicoes', label: 'Número de refeições sólidas por dia', type: 'choice', options: ['1 refeição', '2 refeições', '3 ou mais refeições'] },
      { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia', 'Fezes com alimentos não digeridos'] },
      { key: 'agua', label: 'Ingestão de água', type: 'choice', options: ['Bebe bem', 'Bebe pouco', 'Não bebe ainda'] },
      { key: 'peso_bebe', label: 'Pesagem recente', type: 'number', unit: 'kg', optional: true },
      { key: 'sentimento_mae', label: 'Como te sentiste tu esta semana com a introdução alimentar?', type: 'scale', min: 'Muito ansiosa', max: 'Muito tranquila' },
      { key: 'partilha', label: 'Algo que queiras partilhar ou perguntar', type: 'text', optional: true },
    ],
  },
  emagrecimento: {
    greeting: (name) => `Olá, ${name} 💛\nHoje é dia de feedback.\nEste momento serve para avaliarmos a tua semana com atenção e ajustar o que for necessário.\nPartilha o que conseguires. Não há respostas certas ou erradas.`,
    fields: [
      { key: 'semana_geral', label: 'Como foi a tua semana em geral?', type: 'scale', min: 'Muito difícil', max: 'Muito boa' },
      { key: 'peso', label: 'Peso desta semana', type: 'number', unit: 'kg', optional: true },
      { key: 'cumprimento', label: 'Cumprimento do plano alimentar', type: 'scale', min: 'Não cumpri', max: 'Cumpri totalmente' },
      { key: 'energia', label: 'Energia', type: 'scale', min: 'Sem energia', max: 'Com muita energia' },
      { key: 'fome', label: 'Fome ao longo do dia', type: 'choice', options: ['Muita fome', 'Fome normal', 'Pouco apetite', 'Irregular'] },
      { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
      { key: 'exercicio', label: 'Movimento ou exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não esta semana'] },
      { key: 'partilha', label: 'Algo que queiras partilhar ou perguntar', type: 'text', optional: true },
    ],
  },
};

// ─── FIELD RENDERERS ───
function ScaleField({ field, value, onChange }) {
  return (
    <div>
      <div className="flex gap-1.5 justify-center my-2">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => onChange(n)} className={`w-11 h-11 rounded-2xl text-sm font-semibold transition-all ${value === n ? 'bg-brand-green text-white scale-110' : 'bg-white border border-brand-border text-brand-muted'}`}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between"><span className="text-[10px] text-brand-muted">{field.min}</span><span className="text-[10px] text-brand-muted">{field.max}</span></div>
    </div>
  );
}

function ChoiceField({ field, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {field.options.map(o => (
        <button key={o} onClick={() => onChange(o)} className={`px-3 py-2 rounded-2xl text-xs font-medium transition-all ${value === o ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>{o}</button>
      ))}
    </div>
  );
}

function MultiField({ field, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-1.5">
      {field.options.map(o => (
        <button key={o} onClick={() => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o])} className={`px-3 py-2 rounded-2xl text-xs font-medium transition-all ${selected.includes(o) ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>{o}</button>
      ))}
    </div>
  );
}

// ─── MAIN PAGE ───
export default function CheckinPage() {
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [availability, setAvailability] = useState({ available: true });
  const [checkingAvail, setCheckingAvail] = useState(true);
  const [programStatus, setProgramStatus] = useState(null);

  const patientType = user?.patient_type || 'emagrecimento';
  const def = CHECKIN_DEFS[patientType] || CHECKIN_DEFS.emagrecimento;
  const firstName = user?.name?.split(' ')[0] || '';

  const fetchAvailability = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/checkins/availability`); setAvailability(data); } catch {} finally { setCheckingAvail(false); }
  }, []);
  const fetchProgramStatus = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-program-status`); setProgramStatus(data); } catch {}
  }, []);
  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/checkins`);
      setHistory(data);
      const today = new Date().toISOString().split('T')[0];
      const todayCI = data.find(c => c.date === today);
      if (todayCI) {
        setAnswers(todayCI.extra_data || {});
        setSaved(true);
      }
    } catch {}
  }, []);
  useEffect(() => { fetchAvailability(); fetchHistory(); fetchProgramStatus(); }, [fetchAvailability, fetchHistory, fetchProgramStatus]);

  // Block check-ins for archived clients
  if (programStatus?.archived) {
    return (
      <div className="fade-in text-center py-16" data-testid="checkin-archived">
        <h2 className="font-heading font-medium text-xl text-brand-text mb-2">Acompanhamento concluído</h2>
        <p className="text-sm text-brand-muted">O teu programa foi concluído. Os teus dados históricos ficam preservados.</p>
        <p className="text-sm text-brand-muted mt-2">Se quiseres retomar o acompanhamento, contacta a tua nutricionista.</p>
      </div>
    );
  }

  const setAnswer = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/checkins`, {
        checkin_type: patientType,
        cumprimento: answers.cumprimento || answers.semana_geral || 3,
        energia: answers.energia || 3,
        fome: typeof answers.fome === 'number' ? answers.fome : 3,
        sintomas: Array.isArray(answers.sintomas) ? answers.sintomas.join(', ') : (answers.sintomas || ''),
        extra_data: answers,
      });
      toast.success('Check-in guardado');
      setSaved(true);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar');
    } finally { setLoading(false); }
  };

  const isFieldVisible = (field) => {
    if (!field.condition) return true;
    return answers[field.condition.field] === field.condition.value;
  };

  return (
    <div className="fade-in" data-testid="checkin-page">
      <div className="mb-4">
        <h2 className="font-heading font-medium text-xl text-brand-text">Check-in Semanal</h2>
        {availability.designated_day && <p className="text-xs text-brand-green mt-1">Dia de check-in: {availability.designated_day}-feira</p>}
      </div>

      {/* Locked state */}
      {!checkingAvail && !availability.available && !saved && (
        <div className="bg-brand-bg border border-brand-border rounded-3xl p-6 text-center mb-6" data-testid="checkin-locked">
          <Heartbeat size={32} className="mx-auto text-brand-placeholder mb-3" weight="duotone" />
          {availability.done_this_week ? (
            <><p className="text-sm font-medium text-brand-text mb-1">Check-in desta semana concluído</p><p className="text-xs text-brand-muted">O próximo será na próxima semana. Bom trabalho!</p></>
          ) : (
            <><p className="text-sm font-medium text-brand-text mb-1">Ainda não é o teu dia de check-in</p><p className="text-xs text-brand-muted">O teu check-in é à {availability.designated_day}-feira. Volta nesse dia!</p></>
          )}
        </div>
      )}

      {(availability.available || saved || checkingAvail) && (
        <>
          {/* Greeting */}
          <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5">
            <p className="text-sm text-brand-text whitespace-pre-line leading-relaxed">{def.greeting(firstName)}</p>
          </div>

          {/* Fields */}
          <div className="space-y-4 mb-6">
            {def.fields.filter(isFieldVisible).map(field => (
              <div key={field.key} className="bg-white border border-brand-border rounded-2xl p-4">
                <p className="text-sm font-medium text-brand-text mb-2">
                  {field.label}{field.optional && <span className="text-[10px] text-brand-muted ml-1">(opcional)</span>}
                </p>
                {field.type === 'scale' && <ScaleField field={field} value={answers[field.key]} onChange={v => setAnswer(field.key, v)} />}
                {field.type === 'choice' && <ChoiceField field={field} value={answers[field.key]} onChange={v => setAnswer(field.key, v)} />}
                {field.type === 'multi' && <MultiField field={field} value={answers[field.key]} onChange={v => setAnswer(field.key, v)} />}
                {field.type === 'number' && (
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.1" value={answers[field.key] || ''} onChange={e => setAnswer(field.key, e.target.value)} className="w-24 rounded-xl border-brand-border" placeholder="0" />
                    {field.unit && <span className="text-xs text-brand-muted">{field.unit}</span>}
                  </div>
                )}
                {field.type === 'text' && <Textarea value={answers[field.key] || ''} onChange={e => setAnswer(field.key, e.target.value)} className="rounded-xl border-brand-border text-sm resize-none" rows={2} placeholder="Escreve aqui..." />}
              </div>
            ))}
          </div>

          <button onClick={handleSubmit} disabled={loading} className="w-full bg-brand-green text-white hover:bg-brand-green-hover rounded-full px-6 py-3.5 font-medium disabled:opacity-50" data-testid="submit-checkin-btn">
            {loading ? 'A guardar...' : (saved ? 'Atualizar check-in' : 'Enviar check-in')}
          </button>
        </>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Histórico</h3>
          <div className="space-y-2">
            {history.slice(0, 8).map(c => (
              <div key={c.id} className="bg-white border border-brand-border rounded-2xl p-3" data-testid={`checkin-history-${c.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-brand-muted">{fmtDatePt(c.date)}</span>
                  {c.checkin_type && <span className="text-[9px] bg-brand-bg px-2 py-0.5 rounded-full text-brand-muted">{c.checkin_type}</span>}
                </div>
                {c.extra_data?.semana_geral && <p className="text-xs text-brand-text">Semana: {c.extra_data.semana_geral}/5</p>}
                {c.sintomas && <p className="text-[10px] text-brand-muted mt-0.5">{c.sintomas}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
